import type { MutationUpdaterFn } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { ExecutionResult } from 'graphql';
import { useCallback } from 'react';

import type {
  CreateAdsPageAdMutation,
  CreateAdsPageAdWithAssetMutation,
  CreateAdWithBatchAssetsMutation,
  CreateAdWithGatewayAssetsMutation,
  TargetingRuleInput,
  UpdateAdsPageAdMutation,
  UpdateAdsPageWithAssetMutation,
  UpdateAdWithBatchAssetsMutation,
  UpdateAdWithGatewayAssetsMutation,
} from '../../../../apis/graphql';
import { AdStatusV5, getErrorMessageFromGraphQlErrors } from '../../../../apis/graphql';
import type { AdFormValues } from '../../../../common/AdForm/adFormik';
import type { AdsPageAd } from '../../../../common/AdForm/hooks/types';
import type { AdsPageLineItem } from '../../../../common/AdForm/types';
import { writeAdTargetingEmbellishmentFieldsToCache } from '../../../../common/adTargeting';
import { AlertType } from '../../../../common/Alert';
import { openToastAlert } from '../../../../common/ToastAlert/toastAlert';
import { WARNING_ALERT_DESCRIPTIONS, WARNING_ALERT_MESSAGES } from '../../../../constants';
import { useTraffickingTableReload } from '../../../../contexts/TraffickingTableReloadContext';
import type { Asset2Extended, Nullable } from '../../../../models';
import {
  CREATE_AD_WITH_BATCH_ASSETS,
  CREATE_AD_WITH_GATEWAY_ASSETS,
  CREATE_ADS_PAGE_AD,
  CREATE_ADS_PAGE_AD_WITH_ASSET,
  UPDATE_AD_WITH_BATCH_ASSETS,
  UPDATE_AD_WITH_GATEWAY_ASSETS,
  UPDATE_ADS_PAGE_AD,
  UPDATE_ADS_PAGE_AD_WITH_ASSET,
} from '../mutations';
import type { SaveAdReturnType } from '../types';
import { buildAssetCountryCodesInput } from '../util/buildAssetCountryCodesInput';
import { buildGatewayAssetCountryCodesInput } from '../util/buildGatewayAssetCountryCodesInput';
import { getCreateMutation, getUpdateMutation, updateAdInCache, writeAdToCache } from './util';

export type UseSaveAdResult = (
  values: AdFormValues,
  creativeId: Nullable<string>,
  assets?: Asset2Extended[]
) => Promise<SaveAdReturnType>;

export function useSaveAd(
  lineItem: AdsPageLineItem,
  ads: AdsPageAd[],
  targetingRule?: TargetingRuleInput | null,
  selectedAdIds?: string[]
): UseSaveAdResult {
  const [createAd] = useMutation<CreateAdsPageAdMutation>(CREATE_ADS_PAGE_AD);
  const [createAdWithAsset] = useMutation<CreateAdsPageAdWithAssetMutation>(CREATE_ADS_PAGE_AD_WITH_ASSET);
  const [createAdWithBatchAssets] = useMutation<CreateAdWithBatchAssetsMutation>(CREATE_AD_WITH_BATCH_ASSETS);
  const [createAdWithGatewayAssets] = useMutation<CreateAdWithGatewayAssetsMutation>(CREATE_AD_WITH_GATEWAY_ASSETS);
  const [updateAd] = useMutation<UpdateAdsPageAdMutation>(UPDATE_ADS_PAGE_AD);
  const [updateAdWithAsset] = useMutation<UpdateAdsPageWithAssetMutation>(UPDATE_ADS_PAGE_AD_WITH_ASSET);
  const [updateAdWithBatchAssets] = useMutation<UpdateAdWithBatchAssetsMutation>(UPDATE_AD_WITH_BATCH_ASSETS);
  const [updateAdWithGatewayAssets] = useMutation<UpdateAdWithGatewayAssetsMutation>(UPDATE_AD_WITH_GATEWAY_ASSETS);

  // No-op outside the Trafficking page (e.g. the standalone Ads page has no table to reload).
  const { scheduleTableReload } = useTraffickingTableReload();

  const saveAd = useCallback(
    // The "values" object is a snapshot of form state.
    // The snapshot is taken before the creativeId value is updated in the form's submit handler, so we rely on passing the creativeId to this function as an argument.
    // See this answer from the Formik author for more information: https://github.com/formium/formik/issues/839#issuecomment-460702644
    async (values: AdFormValues, creativeId: Nullable<string>, assets?: Asset2Extended[]) => {
      if (!values.creative.id) values.creative.id = creativeId || '';

      const assetCountryCodesInput =
        values.creative.type === 'GATEWAY_GO'
          ? buildGatewayAssetCountryCodesInput(lineItem, assets)
          : buildAssetCountryCodesInput(lineItem, assets);

      // Ad exists (has been created)
      if (!!values?.id) {
        const { mutation, variables } = getUpdateMutation(
          { updateAd, updateAdWithAsset, updateAdWithBatchAssets, updateAdWithGatewayAssets },
          values,
          assetCountryCodesInput,
          targetingRule
        );

        const { errors, data }: ExecutionResult<UpdateAdsPageAdMutation> = await mutation({
          variables,
          update: ((cache, { data }) => {
            updateAdInCache(data, values.id);
            // Refresh CampaignSearcher-derived ad targeting fields locally so the UI doesn't show
            // stale invalid-publisher/invalid-country state between the mutation result and the
            // eventual consumer catch-up.
            if (values.id) {
              writeAdTargetingEmbellishmentFieldsToCache({
                cache,
                adId: values.id,
                submittedAdTermValues: values.targetingTermValues,
                lineItemDisplayPublisherTarget: lineItem.displayPublisherTarget ?? [],
                lineItemCountries: lineItem.countries,
              });
            }
          }) as MutationUpdaterFn<UpdateAdsPageAdMutation>,
        });

        if (errors) throw Error(getErrorMessageFromGraphQlErrors(errors));
        if (!data || !data.updateAd) throw Error('Ad save failed');

        if (
          !!values.deliveryStatus &&
          values.adTagList.some((el) => !el.isVerified) &&
          data?.updateAd?.status === AdStatusV5.Off
        ) {
          openToastAlert({
            alertType: AlertType.WARNING,
            message: WARNING_ALERT_MESSAGES.ACTIONS_REQUIRED,
            description: WARNING_ALERT_DESCRIPTIONS.STATUS_REVERTED,
            duration: 10,
          });
        }

        // Local cache is patched above; additionally schedule a delayed refetch so the
        // searcher-applied table (sort order, filters, searcher-only fields) is reconciled.
        scheduleTableReload();

        return { createdAdTagList: data.updateAd.adTagList };
      } else {
        // We want to:
        // 1. Create the ad
        // 2. Read the existing ad list from the cache
        // 3. Write our new ad into the cache, without refetching the entire list.
        const { mutation, variables } = getCreateMutation(
          { createAd, createAdWithAsset, createAdWithBatchAssets, createAdWithGatewayAssets },
          values,
          lineItem.id,
          assetCountryCodesInput
        );

        const { errors, data }: ExecutionResult<CreateAdsPageAdMutation> = await mutation({ variables });

        if (errors) throw Error(getErrorMessageFromGraphQlErrors(errors));
        if (!data || !data.createAd) throw Error('Ad creation failed');

        writeAdToCache(
          lineItem.id,
          values,
          data,
          lineItem.countries,
          selectedAdIds,
          lineItem.displayPublisherTarget ?? []
        );

        // A newly created row can't be placed into the searcher's sort order/page locally;
        // schedule a delayed refetch so it lands in the correct position.
        scheduleTableReload();

        return { createdAdId: data.createAd.id, createdAdTagList: data.createAd.adTagList };
      }
    },
    [
      createAd,
      createAdWithAsset,
      createAdWithBatchAssets,
      createAdWithGatewayAssets,
      lineItem,
      targetingRule,
      updateAd,
      updateAdWithAsset,
      updateAdWithBatchAssets,
      updateAdWithGatewayAssets,
      selectedAdIds,
      scheduleTableReload,
    ]
  );

  return saveAd;
}
